import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import PropTypes from "prop-types";
import Grid from "@material-ui/core/Grid";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  StyledTableCell,
  StyledTableRow,
  useStyles,
} from "components/profiles/ProfileStyles";
import { GetNumberOfDays } from "utils/dates/GetNumberOfDays";
import DateUtils from "utils/dates/DateUtils";
import { compareStrings } from "utils/sort/strings";
import CustomTableFooter from "../../../components/layout/customComponents/customTableFooter/customTableFooter";
import Actions from "./ScholarshipsRecessesHistoryTableAction";
import { createContent } from "utils/tables/createContent";

function desc(a, b, orderBy) {
  if (a[orderBy] === null) a[orderBy] = "";
  if (b[orderBy] === null) b[orderBy] = "";

  if (a[orderBy] === undefined) a[orderBy] = "";
  if (b[orderBy] === undefined) b[orderBy] = "";

  return compareStrings(a[orderBy].toString(), b[orderBy].toString());
}

const ScholarshipRecessesHistoryTable = props => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rows, setRows] = useState({});
  const [headers, setHeaders] = useState([]);
  const [visibleHeaders, setVisibleHeaders] = useState([]);
  const [sortedFields, setSortedFields] = useState([]);
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("startDate");
  const [activeRows, setActiveRows] = useState(0);
  const [t] = useTranslation();

  React.useEffect(() => {
    setHeaders(props.headers);
    setRows(props.rows);
    setVisibleHeaders(props.visibleHeaders);
    setSortedFields(props.sortedFields);
    const filteredRows = props.rows.filter(row => row.deletedAt === null);
    setActiveRows(filteredRows.length);
  }, [
    props.headers,
    props.mainField,
    props.rows,
    props.visibleHeaders,
    props.rows,
  ]);

  function handlePageChange(event, newPage) {
    setPage(newPage);
  }

  const handleRowsPerPageChange = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === sortedFields[property] && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(sortedFields[property]);
  };

  const UsersTableHead = props => {
    const { order, orderBy, onRequestSort } = props;
    const [t] = useTranslation();
    const createSortHandler = property => event => {
      onRequestSort(event, property);
    };

    return (
      <TableHead>
        <TableRow>
          {visibleHeaders.map((header, index) => (
            <StyledTableCell
              key={index}
              size="small"
              align="left"
              sortDirection={orderBy === sortedFields[index] ? order : false}
            >
              <TableSortLabel
                active={orderBy === sortedFields[index]}
                direction={order}
                onClick={createSortHandler(index)}
                disabled={index === 4}
              >
                {header.toUpperCase()}
                {orderBy === sortedFields[index] ? (
                  <span className={classes.visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </span>
                ) : null}
              </TableSortLabel>
            </StyledTableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  UsersTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(["asc", "desc"]).isRequired,
    orderBy: PropTypes.string.isRequired,
  };

  const getDurationDateString = (startDateString, endDateString) => {
    const startDate = DateUtils.parse(startDateString);
    const endDate = DateUtils.parse(endDateString);
    const dayCount = GetNumberOfDays(startDate, endDate);
    const dayString = dayCount > 1 ? t("GENERAL.days") : t("GENERAL.day");
    return `${DateUtils.format(startDate, "DD/MM/YYYY")} - ${DateUtils.format(
      endDate,
      "DD/MM/YYYY"
    )} (${dayCount} ${dayString})`;
  };

  return (
    <Paper className={classes.root}>
      <Grid className={classes.tableWrapper}>
        <Table className={classes.table} size="small">
          {rows.length > 0 && (
            <UsersTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
          )}
          <TableBody>
            {createContent(rows, headers, order, orderBy, desc)
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map(row =>
                row.deletedAt === null ? (
                  <StyledTableRow key={row.collaboratorName}>
                    <TableCell size="small" style={{ width: "20%" }}>
                      {row["startDate"]
                        ? getDurationDateString(
                            row["startDate"],
                            row["endDate"]
                          )
                        : "-"}
                    </TableCell>

                    <TableCell size="small" style={{ width: "20%" }}>
                      {row["createdAt"]
                        ? DateUtils.formatString(row["createdAt"], "DD/MM/YYYY")
                        : "-"}
                    </TableCell>

                    <TableCell size="small" style={{ width: "20%" }}>
                      {row["operator"] ?? "-"}
                    </TableCell>

                    <TableCell
                      size="small"
                      align="right"
                      style={{ width: "15%" }}
                    >
                      <div
                        style={{
                          marginTop: "auto",
                          marginBottom: "auto",
                          display: "flex",
                          flexWrap: "nowrap ",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Actions
                          item={row}
                          context={props.context}
                          itemList={props.itemList}
                        />
                      </div>
                    </TableCell>
                  </StyledTableRow>
                ) : null
              )}
          </TableBody>
          {rows.length > 0 && (
            <CustomTableFooter
              rowsLength={activeRows}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          )}
        </Table>
      </Grid>
    </Paper>
  );
};

ScholarshipRecessesHistoryTable.propTypes = {
  rows: PropTypes.array.isRequired,
  headers: PropTypes.array.isRequired,
  visibleHeaders: PropTypes.array.isRequired,
  mainField: PropTypes.string.isRequired,
};

export default ScholarshipRecessesHistoryTable;
